/**
 * O HTML que um card pode mostrar.
 *
 * A tradução de um card é guardada como HTML — é assim que a cor, o negrito e
 * o sublinhado que o professor pôs no Word chegam ao aluno. Mas o servidor
 * guarda o que receber, e a tela do aluno punha esse HTML direto na página:
 * um `<img onerror=…>` gravado num card (por uma conta de admin tomada, ou por
 * um pedido direto à API) rodava no navegador de todo aluno que estudasse
 * aquele card. E a sessão é compartilhada com o curso e o Étudie.
 *
 * Aqui o HTML é refeito a partir de uma lista do que um card usa: texto,
 * quebra de linha e formatação de letra. Nenhum atributo do original
 * sobrevive — só a cor, o fundo, o negrito, o itálico e o sublinhado,
 * reescritos por nós. O que não está na lista perde a etiqueta e mantém o
 * texto; o que carrega código ou conteúdo de fora (script, iframe, svg,
 * formulário) some inteiro.
 *
 * É a mesma ideia do `checkedTranslation` do importador, com duas diferenças
 * de propósito: este NUNCA lança (é chamado ao desenhar o card, e um card com
 * tradução vazia não pode derrubar a tela de estudo) e mantém quebra de linha
 * e itálico, que os cards editados à mão usam.
 */

/** Formatação de letra e blocos de texto: o que um card usa. */
const MANTIDAS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'SUB', 'SUP', 'MARK', 'SPAN', 'FONT', 'DIV', 'P']);

/** Somem com tudo o que têm dentro. */
const REMOVIDAS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT', 'SVG', 'MATH',
  'LINK', 'META', 'BASE', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'AUDIO', 'VIDEO',
]);

/** Cor em hexadecimal, rgb()/rgba() ou pelo nome — nada que feche o atributo. */
const COR = /^(#[\da-f]{3,8}|rgba?\([\d.,%\s]+\)|[a-z]{3,20})$/i;

function escapar(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function estiloDe(el: HTMLElement): string {
  const estilo: string[] = [];
  const cor = (el.style?.color || (el.tagName.toUpperCase() === 'FONT' ? el.getAttribute('color') : '') || '').trim();
  if (COR.test(cor)) estilo.push(`color:${cor}`);
  const fundo = (el.style?.backgroundColor || '').trim();
  if (COR.test(fundo)) estilo.push(`background-color:${fundo}`);
  const peso = el.style?.fontWeight || '';
  if (peso === 'bold' || Number(peso) >= 600) estilo.push('font-weight:bold');
  if (el.style?.fontStyle === 'italic') estilo.push('font-style:italic');
  if (/underline/.test(`${el.style?.textDecoration ?? ''} ${el.style?.textDecorationLine ?? ''}`)) {
    estilo.push('text-decoration:underline');
  }
  return estilo.join(';');
}

function refazer(node: Node): string {
  if (node.nodeType === 3) return escapar(node.textContent ?? '');
  if (node.nodeType !== 1) return '';
  const el = node as HTMLElement;
  const etiqueta = el.tagName.toUpperCase();
  if (REMOVIDAS.has(etiqueta)) return '';
  if (etiqueta === 'BR') return '<br>';
  const dentro = Array.from(el.childNodes).map(refazer).join('');
  if (!MANTIDAS.has(etiqueta)) return dentro;
  const nome = etiqueta === 'FONT' ? 'span' : etiqueta.toLowerCase();
  const estilo = estiloDe(el);
  return `<${nome}${estilo ? ` style="${estilo}"` : ''}>${dentro}</${nome}>`;
}

/** O HTML de um card, reduzido ao que um card pode mostrar. Nunca lança. */
export function htmlSeguro(html: string | null | undefined): string {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return Array.from(doc.body.childNodes).map(refazer).join('');
  } catch {
    // Sem como interpretar: vai como texto, que é sempre seguro.
    return escapar(String(html));
  }
}
